// client/src/pages/seo/RestaurantQrCodeTischaufstellerDe.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "Ein QR-Code-Tischaufsteller sollte zuerst praktisch sein, nicht nur dekorativ",
  },
  {
    id: "why",
    label: "Warum sind QR-Code-Tischaufsteller für Restaurants sinnvoll?",
  },
  {
    id: "formats",
    label: "Welche Formate kann ein QR-Code-Tischaufsteller haben?",
  },
  {
    id: "design",
    label: "Was sollte auf einem QR-Code-Tischaufsteller stehen?",
  },
  {
    id: "placement",
    label: "Wo sollte ein QR-Code-Tischaufsteller platziert werden?",
  },
  {
    id: "mistakes",
    label: "Welche Fehler passieren bei QR-Code-Tischaufstellern?",
  },
  {
    id: "recommendation",
    label: "Wie würde ich als Restaurantinhaber starten?",
  },
  {
    id: "next-steps",
    label: "Was sollten Sie jetzt tun?",
  },
  {
    id: "bestplace",
    label: "Wie Best Place bei QR-Code-Tischaufstellern hilft",
  },
  {
    id: "faq",
    label: "FAQ zu QR-Code-Tischaufstellern",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantQrCodeTischaufstellerDe() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Restaurant QR-Code Tischaufsteller: Digitale Speisekarte am Tisch platzieren | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/restaurant-qr-code-table-tent",
          de: "/de/restaurant-qr-code-tischaufsteller",
          no: "/no/restaurant-qr-kode-bordskilt",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant QR-Code Tischaufsteller: digitale Speisekarte richtig
              am Tisch platzieren
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Ein QR-Code-Tischaufsteller ist oft der sichtbarste Einstieg in
                eine digitale Speisekarte. Gäste sehen ihn am Tisch, scannen den
                Code und öffnen die Speisekarte auf dem Smartphone.
              </p>

              <p className="seoHero__text">
                Ein schöner Tischaufsteller allein reicht aber nicht aus. Der
                QR-Code muss groß genug, gut sichtbar, klar erklärt und mit
                einer mobilen Speisekarte verbunden sein, die wirklich aktuell
                ist.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Ein QR-Code-Tischaufsteller sollte zuerst praktisch sein, nicht nur
            dekorativ
          </h2>

          <p>
            Viele Restaurants denken zuerst an das Design: Farbe, Logo, Material
            und Form. Diese Details sind wichtig, aber sie sind nicht die erste
            Priorität. Die erste Priorität ist, ob der Gast den QR-Code schnell
            sehen, verstehen und scannen kann.
          </p>

          <p>
            Ein Tischaufsteller sollte in der echten Restaurantumgebung
            funktionieren: bei echtem Licht, an echten Tischen, mit echten
            Smartphones und mit echten Gästen, die vielleicht nicht wissen, was
            nach dem Scannen passiert.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Schwächer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Der QR-Code ist zu klein.</li>
                <li className="text__seo-li">
                  Der Tischaufsteller steht an einer schlechten Position.
                </li>
                <li className="text__seo-li">
                  Das Material spiegelt Licht zu stark.
                </li>
                <li className="text__seo-li">
                  Der Text sagt nur „Scannen Sie mich“.
                </li>
                <li className="text__seo-li">
                  Der QR-Code öffnet eine schwer lesbare PDF-Datei.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Der QR-Code ist groß und kontrastreich.
                </li>
                <li className="text__seo-li">
                  Der Tischaufsteller ist klar sichtbar.
                </li>
                <li className="text__seo-li">
                  Das Material wird unter Restaurantlicht getestet.
                </li>
                <li className="text__seo-li">Der Text erklärt den Nutzen.</li>
                <li className="text__seo-li">
                  Der QR-Code öffnet eine mobile Menüseite.
                </li>
              </ul>
            </div>
          </div>

          <h3>Zuerst die Speisekarte testen, dann Tischaufsteller produzieren</h3>

          <p>
            Bevor viele Tischaufsteller bestellt werden, sollte die digitale
            Speisekarte auf dem Smartphone funktionieren und der QR-Code an
            echten Tischen getestet werden. So vermeiden Sie schöne Drucksachen,
            die im täglichen Service nicht gut funktionieren.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum sind QR-Code-Tischaufsteller für Restaurants sinnvoll?
            </h2>

            <p>
              Ein Tischaufsteller ist sichtbarer als ein kleiner Aufkleber und
              flexibler als ein festes Schild. Er kann auf Tischen, Theken,
              Außentischen, in Wartebereichen oder in der Nähe des
              Zahlungsbereichs stehen.
            </p>

            <p>
              Besonders nützlich ist er, wenn sich die Speisekarte regelmäßig
              ändert: Tagesgerichte, Getränke, saisonale Angebote, ausverkaufte
              Artikel oder Preisänderungen können online aktualisiert werden,
              ohne den QR-Code neu zu drucken.
            </p>

            <h3>Der Tischaufsteller ist ein kleiner Servicepunkt am Tisch</h3>

            <p>
              Der Tischaufsteller ist nicht nur ein Druckprodukt. Er ist ein
              sichtbarer Einstieg in die digitale Speisekarte des Restaurants.
              Wenn er klar, stabil und leicht zu scannen ist, hilft er Gästen,
              die Speisekarte schneller zu öffnen, und kann wiederholte Fragen
              an das Personal reduzieren.
            </p>

            <p>
              Die Qualität entscheidet sich nicht nur am Tischaufsteller selbst,
              sondern am gesamten Ablauf: Platzierung am Tisch, Größe des
              QR-Codes, Erklärungstext, mobiles Menü-Layout und aktuelle
              Menüinhalte.
            </p>
          </div>
        </section>

        <section id="formats" className="acquaintance">
          <h2>Welche Formate kann ein QR-Code-Tischaufsteller haben?</h2>

          <p>
            Restaurants können zwischen einfachen und professionelleren Formaten
            wählen. Für einen ersten Test ist nicht immer die teuerste Lösung
            notwendig.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Laminierte Karte.</li>
            <li className="text__seo-li">Acrylhalter.</li>
            <li className="text__seo-li">Papier-Kartenhalter.</li>
            <li className="text__seo-li">Holz- oder Metallhalter.</li>
            <li className="text__seo-li">3D-gedruckter Halter.</li>
            <li className="text__seo-li">Gebrandeter Tischaufsteller.</li>
            <li className="text__seo-li">Beidseitiger Aufsteller.</li>
            <li className="text__seo-li">
              Kombination mit einem Tagesangebot.
            </li>
          </ul>

          <p>
            Für den ersten Schritt ist eine austauschbare Karte oft besser als
            ein fester Aufkleber. Wenn Text, Design oder Position nicht
            funktionieren, kann die Karte schnell geändert werden.
          </p>

          <VisualIdea>
            Drei Optionen nebeneinander: laminierte Karte, Acrylhalter und ein
            3D-gedruckter Halter mit austauschbarer QR-Code-Karte.
          </VisualIdea>
        </section>

        <section id="design" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollte auf einem QR-Code-Tischaufsteller stehen?</h2>

            <p>
              Ein guter Tischaufsteller beantwortet sofort eine Frage: Was
              passiert, wenn ich diesen QR-Code scanne?
            </p>

            <p>
              Deshalb sollte der QR-Code von einer kurzen und klaren Botschaft
              begleitet werden. Der Gast sollte nicht raten müssen, ob der Code
              eine Speisekarte, eine Bestellseite, eine Zahlungsseite oder ein
              Bewertungsformular öffnet.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Unklar</strong>
                <p>Scannen Sie mich</p>
              </div>

              <div>
                <strong>Besser</strong>
                <p>Digitale Speisekarte öffnen</p>
              </div>
            </div>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Kurze Überschrift.</li>
              <li className="text__seo-li">Großer QR-Code.</li>
              <li className="text__seo-li">Klarer Call-to-Action.</li>
              <li className="text__seo-li">Restaurant-Logo.</li>
              <li className="text__seo-li">Ruhiger Hintergrund.</li>
              <li className="text__seo-li">Ausreichender Kontrast.</li>
              <li className="text__seo-li">Nicht überladen.</li>
              <li className="text__seo-li">Optionaler Sprachhinweis.</li>
            </ul>

            <h3>Größe und Scan-Abstand sind wichtig</h3>

            <p>
              Bei Tischaufstellern ist es besser, genügend visuellen Abstand um
              den QR-Code zu lassen, statt am absoluten Minimum zu planen. Gäste
              sollten den Code aus einer natürlichen Sitzposition scannen
              können, ohne die Karte stark bewegen zu müssen.
            </p>
          </div>
        </section>

        <section id="placement" className="acquaintance">
          <h2>Wo sollte ein QR-Code-Tischaufsteller platziert werden?</h2>

          <p>
            Die Platzierung entscheidet oft darüber, ob Gäste den QR-Code nutzen.
            Wenn der Tischaufsteller hinter Servietten, neben einer Vase oder im
            Schatten steht, wird er möglicherweise nicht wahrgenommen.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Sichtbar machen:</strong> Nicht zwischen Dekoration,
              Besteck oder Gewürzen verstecken.
            </li>

            <li className="text__seo-li">
              <strong>Nicht zu niedrig platzieren:</strong> Der QR-Code sollte
              mit dem Smartphone leicht zu scannen sein.
            </li>

            <li className="text__seo-li">
              <strong>Reflexionen prüfen:</strong> Lampen, Fenster und Kerzen
              können das Scannen erschweren.
            </li>

            <li className="text__seo-li">
              <strong>Beidseitig denken:</strong> Für größere Tische kann ein
              beidseitiger Tischaufsteller sinnvoll sein.
            </li>

            <li className="text__seo-li">
              <strong>Unter echten Bedingungen testen:</strong> Nicht nur im
              Büro, sondern an einem echten Tisch im Restaurant.
            </li>
          </ol>

          <p>
            Ein Tischaufsteller sollte nicht wie störende Werbung wirken. Er
            sollte sich wie ein natürlicher Teil des Tischservices anfühlen.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche Fehler passieren bei QR-Code-Tischaufstellern?</h2>

            <p>
              Viele Probleme entstehen, wenn der Tischaufsteller nur als
              Drucksache geplant wird und nicht als Teil der Gästeerfahrung.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Häufige Fehler</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Der QR-Code wird gedruckt, bevor die Speisekarte getestet
                    wurde.
                  </li>
                  <li className="text__seo-li">
                    Der Code ist zu klein oder zu stark gestaltet.
                  </li>
                  <li className="text__seo-li">
                    Glänzendes Material spiegelt das Restaurantlicht.
                  </li>
                  <li className="text__seo-li">
                    Der Tischaufsteller steht an einer schlechten Position.
                  </li>
                  <li className="text__seo-li">
                    Der Text erklärt den Zweck nicht.
                  </li>
                  <li className="text__seo-li">
                    Der QR-Code öffnet eine alte PDF-Datei.
                  </li>
                  <li className="text__seo-li">
                    Es gibt keine Ersatzkarten für beschädigte Tischaufsteller.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Besser</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Zuerst die digitale Speisekarte testen.
                  </li>
                  <li className="text__seo-li">
                    Einen großen, kontrastreichen QR-Code drucken.
                  </li>
                  <li className="text__seo-li">
                    Material unter echtem Licht prüfen.
                  </li>
                  <li className="text__seo-li">
                    Platzierung am Tisch testen.
                  </li>
                  <li className="text__seo-li">
                    Klar schreiben: „Digitale Speisekarte öffnen“.
                  </li>
                  <li className="text__seo-li">
                    Einen stabilen Menü-Link verwenden.
                  </li>
                  <li className="text__seo-li">
                    Einige Ersatzkarten bereithalten.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Wie würde ich als Restaurantinhaber starten?</h2>

          <p>
            Ich würde nicht sofort eine große Menge professioneller
            Tischaufsteller bestellen. Ich würde mit einem kleinen Test starten.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Digitale Speisekarte vorbereiten:</strong> Die
              Speisekarte auf dem Smartphone prüfen.
            </li>

            <li className="text__seo-li">
              <strong>QR-Code testen:</strong> Mit iPhone und Android öffnen.
            </li>

            <li className="text__seo-li">
              <strong>2 bis 3 Test-Tischaufsteller erstellen:</strong> Einfach,
              aber gut lesbar.
            </li>

            <li className="text__seo-li">
              <strong>Im Restaurant platzieren:</strong> An echten Tischen und
              unter echtem Licht.
            </li>

            <li className="text__seo-li">
              <strong>Personal fragen:</strong> Welche Fragen stellen Gäste?
            </li>

            <li className="text__seo-li">
              <strong>Finale Version später produzieren:</strong> Eine größere
              Menge erst nach dem Test bestellen.
            </li>
          </ol>

          <p>
            Das reduziert das Risiko, schöne Tischaufsteller zu haben, die im
            täglichen Restaurantbetrieb nicht gut funktionieren.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Was sollten Sie jetzt tun?</h2>

            <p>
              Wenn Sie QR-Code-Tischaufsteller für Ihr Restaurant erstellen
              möchten, starten Sie mit einem einfachen praktischen Test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Zuerst eine digitale Speisekarte erstellen.
              </li>
              <li className="text__seo-li">
                Die Speisekarte auf dem Smartphone öffnen.
              </li>
              <li className="text__seo-li">
                Einen QR-Code mit stabilem Link erstellen.
              </li>
              <li className="text__seo-li">
                2 bis 3 Testkarten drucken.
              </li>
              <li className="text__seo-li">
                Diese an echten Tischen platzieren.
              </li>
              <li className="text__seo-li">
                Licht, Scanbarkeit und Verständlichkeit prüfen.
              </li>
              <li className="text__seo-li">
                Die finale Version erst nach dem Test bestellen.
              </li>
            </ul>

            <VisualIdea>
              Ein kleiner Tischaufsteller-Test: eine mobile Speisekarte, ein
              stabiler QR-Code, zwei oder drei gedruckte Karten und echte
              Platzierung am Tisch vor der finalen Produktion.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ zu QR-Code-Tischaufstellern</h2>

          <div className="seoFaq">
            <details>
              <summary>Was ist ein QR-Code-Tischaufsteller?</summary>
              <p>
                Das ist eine kleine Karte oder ein Halter auf dem Restauranttisch,
                mit dem Gäste die digitale Speisekarte durch Scannen eines
                QR-Codes öffnen können.
              </p>
            </details>

            <details>
              <summary>Was sollte ein Tischaufsteller enthalten?</summary>
              <p>
                Einen großen QR-Code, eine klare Botschaft wie „Digitale
                Speisekarte öffnen“ und optional ein Logo oder einen
                Sprachhinweis.
              </p>
            </details>

            <details>
              <summary>
                Muss ich den QR-Code neu drucken, wenn sich die Speisekarte
                ändert?
              </summary>
              <p>
                Nein, wenn der QR-Code auf einen stabilen Menü-Link führt. Die
                Inhalte der Speisekarte können online aktualisiert werden.
              </p>
            </details>

            <details>
              <summary>Welches Material ist am besten?</summary>
              <p>
                Für Tests reichen laminierte Karten oder einfache Halter aus.
                Für den langfristigen Einsatz sind stabile und leicht zu
                reinigende Materialien besser.
              </p>
            </details>

            <details>
              <summary>Ist ein Aufkleber besser als ein Tischaufsteller?</summary>
              <p>
                Für Tests ist ein Tischaufsteller flexibler, weil er leichter
                bewegt, ersetzt oder neu gestaltet werden kann.
              </p>
            </details>

            <details>
              <summary>Kann ich QR-Code-Tischaufsteller selbst erstellen?</summary>
              <p>
                Ja. Sie können Karten drucken, laminieren oder in einfache
                Halter stellen. Wichtig ist vor allem, dass der QR-Code gut
                scanbar bleibt.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="qr"
          exclude="/de/restaurant-qr-code-tischaufsteller"
        />
      </article>
    </main>
  );
}